import { useEffect, useMemo } from "react";
import { useLocale, useTranslations } from "next-intl";
import { type OptionItem, type OptionListMap } from "@/core/cms/option-lists";
import { useOptionLists } from "@/core/cms/option-lists-context";
import { LogEvent } from "@/datadog/events";
import { sendClientLog } from "@/datadog/utils/send-client-log";

export type ConstantOptions = { [key: string]: string };

const EMPTY_OPTIONS: ConstantOptions = Object.freeze({});

/** Static reference data: always the locale JSON, even if Strapi has a list. */
const LOCALE_ONLY_KEYS: ReadonlySet<string> = new Set(["countries"]);

export const pickCmsOptions = (
	lists: OptionListMap,
	key: string
): OptionItem[] | undefined =>
	LOCALE_ONLY_KEYS.has(key) ? undefined : lists[key];

/** Collates per locale, so Swedish sorts å, ä and ö after z. */
export const sortOptionsByLabel = (
	options: OptionItem[],
	locale: string
): OptionItem[] => {
	const collator = new Intl.Collator(locale, { sensitivity: "base" });
	return [...options].sort((a, b) => collator.compare(a.label, b.label));
};

/**
 * Narrows the raw `options` message. next-intl resolves an unknown namespace to
 * its own key path, and iterating that string yields one option per character.
 */
export const toConstantOptions = (raw: unknown): ConstantOptions | null => {
	if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
		return null;
	}
	return Object.values(raw).every((value) => typeof value === "string")
		? (raw as ConstantOptions)
		: null;
};

/**
 * Reads the `options` object from a `constants.<key>` translation namespace.
 *
 * next-intl types both the namespace argument and `raw()` against the message
 * catalogue, but here the namespace is dynamic and `options` holds a
 * `{ value: label }` dictionary rather than a string. This hook centralizes
 * those two typed escape hatches instead of repeating them at every call site.
 */
const useLocaleConstantOptions = (key: string): ConstantOptions => {
	const t = useTranslations(`constants.${key}` as never);
	const options = toConstantOptions(
		(t.raw as (path: string) => unknown)("options")
	);
	const missing = options === null;

	useEffect(() => {
		if (!missing) {
			return;
		}
		void sendClientLog({
			level: "warn",
			event: LogEvent.ConstantOptionsMissing,
			message: "Constants namespace holds no options dictionary",
			context: { key },
		});
	}, [key, missing]);

	return options ?? EMPTY_OPTIONS;
};

/**
 * Strapi's list for a constants key, or undefined when the CMS has none. These
 * values are submitted to FLOW, so an absent list falls back to the locale JSON
 * rather than rendering an empty dropdown.
 */
const useCmsOptions = (key: string): OptionItem[] | undefined =>
	pickCmsOptions(useOptionLists(), key);

/** The `{ value: label }` dictionary, from Strapi when it has the list. */
export const useConstantOptions = (key: string): ConstantOptions => {
	const localeOptions = useLocaleConstantOptions(key);
	const cmsOptions = useCmsOptions(key);

	return useMemo(() => {
		if (!cmsOptions) {
			return localeOptions;
		}
		return Object.fromEntries(
			cmsOptions.map(({ value, label }) => [value, label])
		);
	}, [cmsOptions, localeOptions]);
};

/**
 * Same source as {@link useConstantOptions}, but mapped into the
 * `{ value, label }[]` shape expected by `MultiSelect`/`MultiSelectField`.
 * Memoized so callers can safely use the list as an effect dependency.
 */
export const useConstantSelectOptions = (key: string): OptionItem[] => {
	const localeOptions = useLocaleConstantOptions(key);
	const cmsOptions = useCmsOptions(key);

	return useMemo(
		() =>
			cmsOptions ??
			Object.entries(localeOptions).map(([value, label]) => ({ value, label })),
		[cmsOptions, localeOptions]
	);
};

export const useSortedConstantSelectOptions = (key: string): OptionItem[] => {
	const options = useConstantSelectOptions(key);
	const locale = useLocale();

	return useMemo(() => sortOptionsByLabel(options, locale), [options, locale]);
};
